import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, saveUserData, clearAuth } from '../utils/userData'

const FacilitatorDashboard = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    setIsAuthenticated(true)
    fetchUserData()
  }, [navigate])

  const fetchUserData = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/auth/me')
      if (response.data.success) {
        const data = response.data.data
        setUserData(data)
        saveUserData(data)
        
        // Redirect non-facilitators to participant dashboard
        if (!data.isFacilitator) {
          navigate('/dashboard')
          return
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err)
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated || !userData) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
                Facilitator Dashboard
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Welcome back, {userData?.name?.split(' ')[0] || 'Facilitator'}!
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Manage your facilitator profile and connect with the Wellness Loop community.
              </p>
            </div>
            {userData?.facilitatorSlug && (
              <div className="flex flex-col items-start md:items-end gap-3 bg-gradient-to-br from-primary-50 to-white p-5 rounded-2xl border border-primary-100 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-primary-700 font-semibold uppercase tracking-wide">Your Public Profile</span>
                </div>
                <Link 
                  to={`/facilitator/${userData.facilitatorSlug}`}
                  className="group flex items-center gap-3 px-6 py-4 bg-white border-2 border-primary-300 rounded-xl font-medium hover:bg-primary-50 hover:border-primary-500 transition-all shadow-md hover:shadow-xl"
                >
                  <div className="flex items-center gap-1.5 text-gray-600 group-hover:text-primary-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span className="text-sm">/facilitator/</span>
                  </div>
                  <span className="text-lg text-primary-700 font-bold group-hover:text-primary-800">{userData.facilitatorSlug}</span>
                  <svg className="w-5 h-5 text-primary-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">⚡</span>
            Quick Actions
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Edit Profile Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/facilitators/edit-profile">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-purple-500 to-indigo-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">✏️</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      Edit Profile
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Update your profile information, tags, and visibility settings.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* Facilitator Directory Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/facilitators">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">📋</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      Facilitator Directory
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Browse and connect with other facilitators in the community.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* My Events Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/facilitator/events">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-orange-500 to-amber-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">📅</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      My Events
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Create and manage your wellness events and sessions.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* My Consultations Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/facilitator/consultations">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-teal-500 to-cyan-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">🗓️</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      My Consultations
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Create consultation offerings and view interest from potential clients.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* My Classes Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Link to="/facilitator/classes">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-pink-500 to-rose-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">🧘</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      My Classes
                    </h3>
                    <p className="text-gray-600 text-sm">
                      List your recurring classes and track interest from participants.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* Gallery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link to="/facilitator/gallery">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-sky-500 to-blue-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">🖼️</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      My Gallery
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Upload photos and videos that appear on your public profile.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* Testimonials Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Link to="/facilitator/testimonials">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-yellow-500 to-amber-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">⭐</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      Testimonials
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Invite clients to share feedback and manage what shows on your profile.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>

            {/* Resources Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <Link to="/facilitator/resources">
                <Card className="h-full hover:shadow-xl transition-all bg-white overflow-hidden group cursor-pointer">
                  <div className="h-2 bg-gradient-to-r from-violet-500 to-purple-600" />
                  <div className="p-6">
                    <div className="text-4xl mb-4">📚</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      Resources
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Share PDFs, videos, and links with your clients.
                    </p>
                  </div>
                </Card>
              </Link>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  )
}

export default FacilitatorDashboard

